import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { movies, genres, regions, years } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import FilterPanel from './components/FilterPanel';
import type { TypeFilter } from './components/FilterPanel';

const PAGE_SIZE = 12;

const typeTabs: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'movie', label: '电影' },
  { value: 'tv', label: '电视剧' },
];

const typeCopy: Record<TypeFilter, { title: string; desc: string }> = {
  all: {
    title: '全部影片',
    desc: '电影与电视剧的统一索引，按类型、地区、年份筛选，快速定位想看的内容。',
  },
  movie: {
    title: '电影分类',
    desc: '从类型片到作者电影，按题材、地区与上映年份整理的电影资料库。',
  },
  tv: {
    title: '电视剧分类',
    desc: '涵盖悬疑、都市、年代与治愈题材的剧集索引，附集数与剧情简介。',
  },
};

function readList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  return raw.split(',').filter(Boolean);
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const rawType = searchParams.get('type');
  const type: TypeFilter = rawType === 'movie' || rawType === 'tv' ? rawType : 'all';
  const selectedGenres = readList(searchParams, 'genres');
  const selectedRegions = readList(searchParams, 'regions');
  const selectedYears = readList(searchParams, 'years').map(Number).filter((year) => !Number.isNaN(year));
  const selectedTag = searchParams.get('tag') ?? 'all';
  const sort = searchParams.get('sort') ?? 'latest';
  const pageParam = Number(searchParams.get('page') ?? '1');
  const page = Number.isFinite(pageParam) && pageParam > 0 ? Math.floor(pageParam) : 1;

  const copy = typeCopy[type];

  usePageMeta(
    `${copy.title} - 青苹果影院`,
    `${copy.desc} 支持类型、地区、年份多条件筛选，并提供最新、热门与评分排序。`,
    `${copy.title},影片筛选,电影分类,电视剧分类,青苹果影院`,
  );

  const updateParams = (updater: (params: URLSearchParams) => void, keepPage = false) => {
    const next = new URLSearchParams(searchParams);
    updater(next);
    if (!keepPage) next.delete('page');
    setSearchParams(next);
  };

  const toggleValue = (key: string, value: string) => {
    updateParams((params) => {
      const set = new Set(readList(params, key));
      if (set.has(value)) set.delete(value);
      else set.add(value);
      if (set.size === 0) params.delete(key);
      else params.set(key, Array.from(set).join(','));
    });
  };

  const handleTypeChange = (value: TypeFilter) => {
    updateParams((params) => {
      if (value === 'all') params.delete('type');
      else params.set('type', value);
    });
  };

  const handleTagChange = (value: string) => {
    updateParams((params) => {
      if (value === 'all') params.delete('tag');
      else params.set('tag', value);
    });
  };

  const handleSortChange = (value: string) => {
    updateParams((params) => {
      if (value === 'latest') params.delete('sort');
      else params.set('sort', value);
    });
  };

  const handleReset = () => {
    setSearchParams(new URLSearchParams());
    setFiltersOpen(false);
  };

  const handlePageChange = (nextPage: number) => {
    updateParams((params) => {
      if (nextPage <= 1) params.delete('page');
      else params.set('page', `${nextPage}`);
    }, true);
    window.scrollTo({ top: 320, behavior: 'smooth' });
  };

  const filtered = useMemo(() => {
    let result = movies.filter((movie) => {
      if (type !== 'all' && movie.category !== type) return false;
      if (selectedGenres.length > 0 && !movie.genres.some((genre) => selectedGenres.includes(genre))) {
        return false;
      }
      if (selectedRegions.length > 0 && !selectedRegions.includes(movie.region)) return false;
      if (selectedYears.length > 0 && !selectedYears.includes(movie.year)) return false;
      if (selectedTag === 'hot' && !movie.isHot) return false;
      if (selectedTag === 'new' && !movie.isNew) return false;
      return true;
    });

    result = [...result].sort((a, b) => {
      if (sort === 'rating') return b.rating - a.rating;
      if (sort === 'popular') return b.popularity - a.popularity;
      return b.releaseDate.localeCompare(a.releaseDate);
    });

    return result;
  }, [type, selectedGenres, selectedRegions, selectedYears, selectedTag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageMovies = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const activeFilterCount =
    selectedGenres.length + selectedRegions.length + selectedYears.length + (selectedTag === 'all' ? 0 : 1);

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-11 flex items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-700 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm" aria-hidden="true" />
          <span className="text-foreground-800">影片分类</span>
          {type !== 'all' && (
            <>
              <i className="ri-arrow-right-s-line text-sm" aria-hidden="true" />
              <span className="text-foreground-800">{type === 'movie' ? '电影' : '电视剧'}</span>
            </>
          )}
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-8 md:pt-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground-950 tracking-tight">{copy.title}</h1>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-2xl">{copy.desc}</p>
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
            {typeTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => handleTypeChange(tab.value)}
                aria-pressed={type === tab.value}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  type === tab.value
                    ? 'bg-background-50 text-primary-700 border border-background-200'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 md:mt-8">
          <div className="flex items-center justify-between gap-3 mb-3 md:hidden">
            <button
              type="button"
              onClick={() => setFiltersOpen((open) => !open)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 cursor-pointer"
              aria-expanded={filtersOpen}
            >
              <i className="ri-equalizer-line text-base text-primary-600" aria-hidden="true" />
              筛选条件
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-[11px] flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <span className="text-xs text-foreground-500">
              共 <strong className="text-foreground-900 font-medium">{filtered.length}</strong> 部
            </span>
          </div>

          <div className={filtersOpen ? 'block' : 'hidden md:block'}>
            <FilterPanel
              genres={genres}
              regions={regions}
              years={years}
              selectedGenres={selectedGenres}
              selectedRegions={selectedRegions}
              selectedYears={selectedYears}
              selectedTag={selectedTag}
              sort={sort}
              onToggleGenre={(value) => toggleValue('genres', value)}
              onToggleRegion={(value) => toggleValue('regions', value)}
              onToggleYear={(value) => toggleValue('years', `${value}`)}
              onChangeTag={handleTagChange}
              onChangeSort={handleSortChange}
              onReset={handleReset}
              onClose={() => setFiltersOpen(false)}
            />
          </div>
        </div>

        <div className="mt-6 md:mt-8 flex items-center justify-between gap-3">
          <p className="text-xs md:text-sm text-foreground-500">
            共找到 <strong className="text-foreground-900 font-medium">{filtered.length}</strong> 部作品
            {activeFilterCount > 0 && <span className="ml-2 text-foreground-400">已应用 {activeFilterCount} 个筛选条件</span>}
          </p>
          <p className="text-xs text-foreground-400 font-label">
            第 {currentPage} / {totalPages} 页
          </p>
        </div>

        {pageMovies.length > 0 ? (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-6 md:gap-x-4 md:gap-y-8">
            {pageMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} showCategory />
            ))}
          </div>
        ) : (
          <div className="mt-4">
            <EmptyState
              icon="ri-search-eye-line"
              title="无符合条件的影片"
              description="当前筛选条件组合下没有找到作品，可以尝试减少条件或直接重置筛选。"
              actionLabel="重置筛选条件"
              onAction={handleReset}
            />
          </div>
        )}

        {filtered.length > 0 && (
          <div className="mt-8 md:mt-10 pb-14 md:pb-16">
            <Pagination page={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        )}
      </div>
    </div>
  );
}